import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ThumbsUp, Flag } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation.jsx';

/**
 * PainPointVotePanel — the room's vote, on the map, for one scenario.
 *
 * While the vote is open a scenario track shows the pain-point groups born
 * under it instead of an empty map. Each device taps its votes (a budget,
 * one per group); counts climb live. "Päätä äänestys" closes the vote,
 * counts it and promotes each scenario's winners to muuttujat, and the map
 * fills in. The button is on every screen for now; the route behind it is
 * admin-only, so only a facilitator's tap does anything.
 */

const Panel = styled.div`
  position: absolute;
  inset: 16px;
  z-index: 40;
  overflow-y: auto;
  padding: 24px 28px 32px;
  border-radius: 14px;
  background: rgba(10, 14, 22, 0.94);
  border: 1px solid var(--surface-border, #2c3444);
  color: var(--text-on-dark, #F2F2F2);
  font-family: var(--font-sans);
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px 24px;
  margin-bottom: 18px;
  h2 { margin: 0; font-size: 1.3rem; }
  p { margin: 4px 0 0; color: var(--text-muted-on-dark, #B8B8B8); }
`;

const Budget = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  color: ${(p) => (p.$left ? '#0b0d12' : 'var(--text-muted-on-dark, #98a2b3)')};
  background: ${(p) => (p.$left ? 'var(--color-accent, #f2c14e)' : 'transparent')};
  border: 1px solid ${(p) => (p.$left ? 'var(--color-accent, #f2c14e)' : 'var(--surface-border, #2c3444)')};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
`;

const Card = styled.button`
  all: unset;
  cursor: ${(p) => (p.$disabled ? 'default' : 'pointer')};
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$mine ? 'var(--color-accent, #f2c14e)' : 'var(--surface-border, #2c3444)')};
  background: ${(p) => (p.$mine ? 'rgba(255, 225, 0, 0.08)' : 'rgba(255, 255, 255, 0.03)')};
  opacity: ${(p) => (p.$disabled && !p.$mine ? 0.6 : 1)};
  &:hover { border-color: ${(p) => (p.$disabled ? '' : 'var(--color-accent, #f2c14e)')}; }
`;

const Title = styled.div`
  font-weight: 700;
  line-height: 1.3;
`;

const Count = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  color: ${(p) => (p.$mine ? 'var(--color-accent, #f2c14e)' : 'var(--text-muted-on-dark, #98a2b3)')};
`;

const Members = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: var(--text-muted-on-dark, #98a2b3);
  font-size: 0.86rem;
  line-height: 1.4;
  li + li { margin-top: 2px; }
`;

const Finish = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 22px;
  padding: 10px 16px;
  border-radius: 10px;
  font-weight: 700;
  background: var(--color-accent, #f2c14e);
  color: #0b0d12;
  &:disabled { opacity: 0.6; cursor: default; }
`;

const Note = styled.p`
  margin: 8px 0 0;
  color: ${(p) => (p.$tone === 'error' ? '#FF8A85' : 'var(--text-muted-on-dark, #98a2b3)')};
`;

export default function PainPointVotePanel({ instanceId, scenarioId, scenarioTitle, participantId, onFinished }) {
  const { t } = useTranslation();
  const headers = participantId ? { 'X-Participant-Id': participantId, Accept: 'application/json' } : { Accept: 'application/json' };
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    fetch(API_ENDPOINTS.PREWORK_VOTES(instanceId), { credentials: 'include', headers })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setData)
      .catch((e) => setError(e.message));
  }, [instanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); const timer = setInterval(load, 15000); return () => clearInterval(timer); }, [load]);

  const spent = data ? Object.keys(data.myVotes || {}).length : 0;
  const left = data ? Math.max(0, data.budget - spent) : 0;
  const clusters = (data?.clusters || []).filter((c) => c.status === 'proposed' && (!scenarioId || c.scenarioId === scenarioId));

  const vote = async (clusterId) => {
    if (!data?.open || !data.canVote) return;
    const mine = !!data.myVotes[clusterId];
    if (!mine && left === 0) return;
    setError(null);
    try {
      const r = await fetch(API_ENDPOINTS.PREWORK_VOTE(instanceId, clusterId), { method: 'POST', credentials: 'include', headers });
      const next = await r.json();
      if (!r.ok) throw new Error(next.code === 'BUDGET' ? t('painPointVote.noVotesLeft') : next.code === 'CLOSED' ? t('painPointVote.closed') : next.error);
      setData((d) => ({ ...d, counts: next.counts, myVotes: next.myVotes }));
    } catch (e) { setError(e.message); }
  };

  const finish = async () => {
    setFinishing(true); setError(null);
    try {
      const r = await fetch(API_ENDPOINTS.PREWORK_FINISH_VOTING(instanceId), { method: 'POST', credentials: 'include', headers });
      const next = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(r.status === 401 || r.status === 403 ? t('impactPath.painPointVote.facilitatorOnly') : (next.error || `HTTP ${r.status}`));
      setResult(next);
      onFinished?.(next);
    } catch (e) { setError(e.message); } finally { setFinishing(false); }
  };

  if (!data && !error) return null;
  return (
    <Panel>
      <Head>
        <div>
          <h2>{t('impactPath.painPointVote.title', { scenario: scenarioTitle || '' })}</h2>
          <p>{t('painPointVote.intro', { budget: data?.budget ?? 3 })}</p>
        </div>
        {data?.open && <Budget $left={left > 0}><ThumbsUp size={15} /> {t('painPointVote.left', { left, budget: data.budget })}</Budget>}
      </Head>
      {error && <Note $tone="error">{error}</Note>}
      {!clusters.length && <Note>{t('painPointVote.empty')}</Note>}
      <Grid>
        {clusters.map((c) => {
          const mine = !!data?.myVotes?.[c.id];
          const disabled = !data?.open || !data?.canVote || (!mine && left === 0);
          return (
            <Card key={c.id} type="button" onClick={() => vote(c.id)} $mine={mine} $disabled={disabled} aria-pressed={mine} disabled={disabled}>
              <Title>{c.title}</Title>
              <Count $mine={mine}><ThumbsUp size={14} /> {data?.counts?.[c.id] || 0}</Count>
              <Members>{(c.members || []).slice(0, 4).map((m, i) => <li key={i}>{m.text}</li>)}</Members>
            </Card>
          );
        })}
      </Grid>
      {data?.open && !result && (
        <Finish type="button" onClick={finish} disabled={finishing}>
          <Flag size={16} /> {finishing ? t('impactPath.painPointVote.finishing') : t('impactPath.painPointVote.finish')}
        </Finish>
      )}
      {result && <Note>{t('impactPath.painPointVote.finished', { count: result.promoted?.length || 0, votes: result.totalVotes || 0 })}</Note>}
    </Panel>
  );
}
